import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './HomePageCMS.css';
import '../../styles/AdminShared.css';

const API = process.env.REACT_APP_API_URL || '/api';

interface FAQItem { question: string; question_bn: string; answer: string; answer_bn: string; }
interface FAQCategory { category: string; category_bn: string; items: FAQItem[]; }

interface FAQContent {
  hero_title: string;
  hero_title_bn: string;
  hero_subtitle: string;
  hero_subtitle_bn: string;
  faq_items: FAQCategory[];
  cta_title: string;
  cta_title_bn: string;
  cta_subtitle: string;
  cta_subtitle_bn: string;
}

const EMPTY: FAQContent = {
  hero_title: '', hero_title_bn: '', hero_subtitle: '', hero_subtitle_bn: '',
  faq_items: [],
  cta_title: '', cta_title_bn: '', cta_subtitle: '', cta_subtitle_bn: '',
};

const FAQPageCMS: React.FC = () => {
  const [content, setContent] = useState<FAQContent>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/faq/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setContent(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContent(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /* Category helpers */
  const addCategory = () => setContent(prev => ({
    ...prev, faq_items: [...prev.faq_items, { category: '', category_bn: '', items: [] }],
  }));

  const removeCategory = (ci: number) => setContent(prev => ({
    ...prev, faq_items: prev.faq_items.filter((_, i) => i !== ci),
  }));

  const updateCategory = (ci: number, field: string, value: string) => {
    const cats = [...content.faq_items];
    cats[ci] = { ...cats[ci], [field]: value };
    setContent(prev => ({ ...prev, faq_items: cats }));
  };

  /* Item helpers */
  const addItem = (ci: number) => {
    const cats = [...content.faq_items];
    cats[ci] = { ...cats[ci], items: [...cats[ci].items, { question: '', question_bn: '', answer: '', answer_bn: '' }] };
    setContent(prev => ({ ...prev, faq_items: cats }));
  };

  const removeItem = (ci: number, qi: number) => {
    const cats = [...content.faq_items];
    cats[ci] = { ...cats[ci], items: cats[ci].items.filter((_, i) => i !== qi) };
    setContent(prev => ({ ...prev, faq_items: cats }));
  };

  const updateItem = (ci: number, qi: number, field: keyof FAQItem, value: string) => {
    const cats = [...content.faq_items];
    const items = [...cats[ci].items];
    items[qi] = { ...items[qi], [field]: value };
    cats[ci] = { ...cats[ci], items };
    setContent(prev => ({ ...prev, faq_items: cats }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/faq/1/`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (res.ok) { setContent(await res.json()); Swal.fire({ icon: 'success', title: 'Saved!', text: 'FAQ page updated', timer: 1500, showConfirmButton: false }); }
      else throw new Error();
    } catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save' }); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="homepage-cms-container"><div className="cms-card"><div className="card-content">
      <div className="cms-skeleton" style={{ height: 40 }} />
      <div className="cms-skeleton" style={{ height: 40, marginTop: 12 }} />
    </div></div></div>
  );

  return (
    <div className="homepage-cms-container">
      <form onSubmit={handleSubmit}>

        {/* Hero */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\u2753'}</div><h3>Hero Section</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Hero Title (English)</label><input className="form-input" name="hero_title" value={content.hero_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Title (??)</label><input className="form-input" name="hero_title_bn" value={content.hero_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginTop: 8 }}>
              <div className="cms-form-group"><label>Hero Subtitle (English)</label><input className="form-input" name="hero_subtitle" value={content.hero_subtitle} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Subtitle (??)</label><input className="form-input" name="hero_subtitle_bn" value={content.hero_subtitle_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCC2'}</div><h3>FAQ Categories & Items</h3></div>
            <button type="button" className="btn-add-feature" onClick={addCategory}>+ Add Category</button>
          </div>
          <div className="card-content">
            {content.faq_items.length === 0 && <div className="empty-state"><p>No FAQ categories yet. Click "Add Category".</p></div>}
            {content.faq_items.map((cat, ci) => (
              <div key={ci} style={{ marginBottom: 20, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                {/* Category header */}
                <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#64748b' }}>Category #{ci + 1}</span>
                    <button type="button" className="btn-remove-feature" onClick={() => removeCategory(ci)}>{'\u2715'}</button>
                  </div>
                  <div className="cms-grid-2" style={{ gap: 8 }}>
                    <input className="form-input" value={cat.category} onChange={e => updateCategory(ci, 'category', e.target.value)} placeholder="Category name (English)" style={{ fontWeight: 600 }} />
                    <input className="form-input" value={cat.category_bn} onChange={e => updateCategory(ci, 'category_bn', e.target.value)} placeholder="Category name (??)" style={{ fontWeight: 600 }} />
                  </div>
                </div>

                {/* Items */}
                <div style={{ padding: 14 }}>
                  {cat.items.map((item, qi) => (
                    <div key={qi} className="feature-item-card" style={{ marginBottom: 10 }}>
                      <div className="feature-header">
                        <span className="feature-count">Q{qi + 1}</span>
                        <button type="button" className="btn-remove-feature" onClick={() => removeItem(ci, qi)}>{'\u2715'}</button>
                      </div>
                      <div className="cms-grid-2" style={{ marginBottom: 8 }}>
                        <div className="cms-form-group"><label>Question (English)</label><input className="form-input" value={item.question} onChange={e => updateItem(ci, qi, 'question', e.target.value)} /></div>
                        <div className="cms-form-group"><label>Question (??)</label><input className="form-input" value={item.question_bn} onChange={e => updateItem(ci, qi, 'question_bn', e.target.value)} /></div>
                      </div>
                      <div className="cms-grid-2">
                        <div className="cms-form-group"><label>Answer (English)</label><textarea className="form-input" value={item.answer} onChange={e => updateItem(ci, qi, 'answer', e.target.value)} rows={3} style={{ resize: 'vertical' }} /></div>
                        <div className="cms-form-group"><label>Answer (??)</label><textarea className="form-input" value={item.answer_bn} onChange={e => updateItem(ci, qi, 'answer_bn', e.target.value)} rows={3} style={{ resize: 'vertical' }} /></div>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn-add-feature" onClick={() => addItem(ci)} style={{ marginTop: 4 }}>
                    + Add Question
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCE2'}</div><h3>Bottom CTA</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>CTA Title (English)</label><input className="form-input" name="cta_title" value={content.cta_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>CTA Title (??)</label><input className="form-input" name="cta_title_bn" value={content.cta_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>CTA Subtitle (English)</label><input className="form-input" name="cta_subtitle" value={content.cta_subtitle} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>CTA Subtitle (??)</label><input className="form-input" name="cta_subtitle_bn" value={content.cta_subtitle_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        <div className="cms-page-actions">
          <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : '\uD83D\uDCBE Save Changes'}</button>
        </div>
      </form>
    </div>
  );
};

export default FAQPageCMS;
